import { noop } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo } from 'react';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { IconButton } from '../../shared/button/IconButton';
import { Document, Home, Search } from '../../shared/icons';
import { CustomTooltip } from '../../shared/tooltip';
import { PDFSearchInput } from './PDFSearchInput';
import { ControlBarView, getControlBarView, getCurrentPdfPage, setControlBarView } from './store';
import styles from './DocumentViewer.module.scss';
import { SupportDocumentsSelect } from '../analysis/SupportDocumentsSelect';
import { PageLoadingDots } from '../../shared/loading-dots/PageLoadingDots';

interface PDFControlBarProps {
    numPages: number | null;
    pdfLoading: boolean;
    scrollToPage: (page: number) => void;
    toggleThumbnail: () => void;
    showScheduleStartPage?: boolean;
    setSelectedPage?: (page: number | null) => void;
    scheduleStartPage?: number;
    isSupportDocument: boolean;
}

export const PDFControlBar: React.FC<PDFControlBarProps> = ({
    numPages,
    pdfLoading,
    scrollToPage,
    toggleThumbnail,
    showScheduleStartPage = false,
    setSelectedPage = noop,
    scheduleStartPage = 1,
    isSupportDocument
}) => {
    const dispatch = useAppDispatch();
    const currentPage = useAppSelector(getCurrentPdfPage);
    const controlBarView = useAppSelector(getControlBarView);

    const showSupportDropdown = useMemo(() => controlBarView === ControlBarView.SUPPORT_DOCUMENTS, [controlBarView]);
    const supportDocumentIcon = useMemo(() => showSupportDropdown ? Search : Document, [showSupportDropdown]);
    const oppositeControlBarView = useMemo(() => controlBarView === ControlBarView.SEARCH ? ControlBarView.SUPPORT_DOCUMENTS : ControlBarView.SEARCH, [controlBarView]);

    const previous = useCallback(() => scrollToPage(currentPage - 1), [scrollToPage, currentPage]);
    const next = useCallback(() => scrollToPage(currentPage + 1), [scrollToPage, currentPage]);

    const changeControlView = useCallback(() => dispatch(setControlBarView(oppositeControlBarView)), [dispatch, oppositeControlBarView]);

    useEffect(() => {
        if (!isSupportDocument && controlBarView === ControlBarView.SUPPORT_DOCUMENTS) {
            dispatch(setControlBarView(ControlBarView.SEARCH));
        }
    }, [isSupportDocument, dispatch, controlBarView]);

    return (
        <div className={styles.controlBarWrapper}>
            <button className={styles.sideMenuButton} onClick={toggleThumbnail}>
                {Array(4).fill(0).map((val, i) => val + i).map(index => (
                    <div key={index} className={styles.thumbnailTile} />
                ))}
            </button>
            <div className={styles.pdfConfigWrapper}>
                {pdfLoading ? (
                    <PageLoadingDots />
                ) :
                    (
                        <div className={styles.configWrapper}>
                            <div className={styles.paginationWrapper}>
                                <button className={styles.paginationButton} onClick={previous} disabled={currentPage === 1}>{'<'}</button>
                                <div className={styles.pageNumber}>{currentPage} / {numPages}</div>
                                <button className={styles.paginationButton} onClick={next} disabled={currentPage === numPages}>{'>'}</button>
                            </div>
                            {showScheduleStartPage &&
                                <CustomTooltip overlayText='Scroll to the start of schedule' placement='top'>
                                    <div className={styles.iconWrapper}>
                                        <IconButton icon={Home} onClick={() => setSelectedPage(scheduleStartPage)} fontSize={30} />
                                    </div>
                                </CustomTooltip>
                            }
                            {isSupportDocument && <div className={styles.iconWrapper}><IconButton icon={supportDocumentIcon} overlayText={oppositeControlBarView} tooltipPlacement='top' onClick={changeControlView} fontSize={30} /></div>}
                            {showSupportDropdown ? <SupportDocumentsSelect /> : <PDFSearchInput scrollToPage={scrollToPage} showScheduleStartPage={showScheduleStartPage} isSupportDocument={isSupportDocument} />}
                        </div>
                    )}
            </div>
        </div>
    );
};
